import { defineStore } from 'pinia';
import { ICabinet } from '@/storePinia/stores/cabinet/interfaces/ICabinet';
import useLoader from '@use/useLoader/useLoader';
import axios from 'axios';
import { useFooterMiniBannerStore } from '@/storePinia/stores/footerMiniBanner';
import { IProductSize } from '@/storePinia/interfaces/IProduct/IProduct';
import { IBasketProductBase } from '@/storePinia/stores/basket/interfaces/IBasket';

type ICabinetGetters = {
  [key: string]: (state: ICabinet) => string;
};

export type ICabinetActions = {
  loadPageData: (url: string) => Promise<void>;
  showMore: (url: string) => Promise<void>;
  changeSKU: (payload: { result: IBasketProductBase; size: IProductSize }) => Promise<void>;
  deleteViewedProducts: () => Promise<undefined>;
  deleteViewedProductById: (id: string) => Promise<undefined>;
  getCreateClubCard: (formData: FormData) => Promise<void>;
  updatePageData: (payload?: { [key: string]: string }) => Promise<void>;
  updateUserDeliveryAddress: (formData: FormData) => Promise<void>;
  addUserDeliveryAddress: (formData: FormData) => Promise<void>;
  removeUserDeliveryAddress: (id: string) => Promise<void>;
};

const loader = useLoader();

export const useCabinetStore = defineStore<'cabinet', ICabinet, ICabinetGetters, ICabinetActions>(
  'cabinet',
  {
    state: (): ICabinet => ({
      breadcrumbs: [],
      errors: {},
      form: {},
      request: {},
      routes: {},
      socserv: {},
      title: '',
      items: [],
      messages: {},
      pagination: {},
      idNovaPostStorage: 0,
      repairConditions: [],
      serviceConditions: [],
      boxActiveList: {},
      defaultNewPostDepartment: {},
      statuses: [],
      groups: [],
      certificate: {},
      certificates: [],
      coupons: [],
      clubCard: '',
      countries: [],
      currentCity: {},
      menu: [],
      deliveries: [],
      userAddresses: [],
      sort: {},
    }),
    getters: {},
    actions: {
      async loadPageData(url: string) {
        try {
          loader.start();
          const footerMiniBannerStore = useFooterMiniBannerStore();

          const { status, data } = await axios.get(url, { params: { api: 'y' } });

          if (status !== 200) throw new Error(`bad request response status: ${status}`);

          this.$reset(); // reset storage before write a new data.
          this.$patch(data);

          footerMiniBannerStore.refreshMiniBanner(url);
          // dispatch('happyBirthdayBanner/refresh', url);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async showMore(url) {
        try {
          loader.start();

          const { status, data } = await axios.get<Partial<ICabinet>>(url, {
            params: { api: 'y' },
          });

          if (status !== 200) throw new Error(`bad request response status: ${status}`);

          if (Array.isArray(this.items) && Array.isArray(data.items)) {
            const newItems = [...this.items, ...data.items];

            this.$patch({
              items: newItems,
              pagination: data.pagination,
            });
          }
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async changeSKU({ result, size }) {
        try {
          loader.start();
          const url = size.detailPage;
          const { status, data } = await axios.get<{ product: IBasketProductBase }>(url, {
            params: { api: 'y' },
          });

          if (status !== 200) throw new Error(`bad request response status: ${status}`);

          if (Array.isArray(this.items)) {
            this.$patch({
              items: (this.items as IBasketProductBase[]).map((el) =>
                el.id === result.id ? data.product : el
              ),
            });
          }

          // commit('cabinet/setState', newData);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      async deleteViewedProducts() {
        try {
          if (!this.routes.deleteAll) return undefined;
          loader.start();
          const { path, action } = this.routes.deleteAll;

          const { status, data } = await axios.post<
            { action: string },
            {
              status: number;
              data: { items: IBasketProductBase[] };
            }
          >(path, { action });

          if (status !== 200) throw new Error(`bad request response status: ${status}`);

          this.$patch({ items: data.items });
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async deleteViewedProductById(id) {
        try {
          if (!this.routes.deleteProductById) return undefined;
          loader.start();
          const { path, action } = this.routes.deleteProductById;
          const { status, data } = await axios.post<
            { action: string; id: string },
            { data: { items: IBasketProductBase[] }; status: number }
          >(path, {
            action,
            id,
          });

          if (status !== 200) throw new Error(`bad request response status: ${status}`);

          this.$patch({ items: data.items });
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async getCreateClubCard(formData) {
        try {
          if (!this.routes.getCreateClubCard) return undefined;
          loader.start();
          const { path, action } = this.routes.getCreateClubCard;
          formData.set('action', action);
          const { status } = await axios.post(path, formData);

          if (status !== 200) throw new Error(`bad request response status: ${status}`);
          return undefined;
        } catch (err) {
          console.error(err);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async updatePageData(payload) {
        try {
          loader.start();
          const { data } = await axios.get(window.location.href, {
            params: {
              api: 'Y',
              ...payload,
            },
          });

          this.$patch(data);
        } catch (err) {
          console.error(err);
        } finally {
          loader.stop();
        }
      },
      // addresses methods
      async updateUserDeliveryAddress(formData) {
        try {
          if (!this.routes.updateUserDeliveryAddress) return undefined;
          loader.start();
          const { path, action } = this.routes.updateUserDeliveryAddress;

          formData.set('action', action);

          await axios.post(path, formData);
          await this.updatePageData();

          return undefined;
        } catch (error) {
          console.error(error);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async addUserDeliveryAddress(formData) {
        try {
          if (!this.routes.addUserDeliveryAddress) return undefined;
          loader.start();
          const { path, action } = this.routes.addUserDeliveryAddress;
          formData.set('action', action);

          await axios.post(path, formData);
          await this.updatePageData();
          return undefined;
        } catch (error) {
          console.error(error);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      async removeUserDeliveryAddress(id) {
        try {
          if (!this.routes.removeUserDeliveryAddress) return undefined;
          loader.start();
          const { path, action } = this.routes.removeUserDeliveryAddress;

          await axios.post(path, {
            action,
            id,
          });

          await this.updatePageData();
          return undefined;
        } catch (error) {
          console.error(error);
          return undefined;
        } finally {
          loader.stop();
        }
      },
      // addresses methods end
    },
  }
);
